// Type-safe API client. All methods return Promise<T> and throw ApiError on
// non-2xx responses. 401 auto-redirects to /login.

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (res.status === 401) {
    if (!location.pathname.startsWith('/login')) {
      window.location.href = '/login'
    }
    throw new ApiError(401, 'unauthorized')
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, (body as { error?: string } | null)?.error ?? res.statusText, body)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

// ─── Types ────────────────────────────────────────────────────────────────

export interface User {
  id: number
  email: string
  name: string
}

export interface Stats {
  users: number
  active_sessions: number
}

export interface VersionInfo {
  version?: string
  commit: string
  /** The first seven characters of `commit` (the server cuts it). */
  commit_short?: string
  built_at: string
}

// ─── API ──────────────────────────────────────────────────────────────────

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<User>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<User>('/auth/me'),

  // Meta
  health: () => request<{ ok: boolean }>('/api/health'),
  version: () => request<VersionInfo>('/api/version'),
  stats: () => request<Stats>('/api/stats'),

  // adfusion
  googleStatus: () => request<GoogleStatus>('/api/google/status'),
  googleAuthUrl: (ads: boolean) => request<{ url: string }>(`/api/google/auth-url?ads=${ads ? 1 : 0}`),
  googleConnect: (body: { code: string; admob_pub_id?: string; ads_customer?: string; ads_dev_token?: string; ads_login_customer?: string }) =>
    request<{ ok: boolean }>('/api/google/connect', { method: 'POST', body: JSON.stringify(body) }),
  googleDisconnect: () =>
    request<{ ok: boolean }>('/api/google/disconnect', { method: 'POST' }),
  setAdmobAccount: (pub: string) =>
    request<{ ok: boolean }>('/api/google/admob-account', { method: 'PUT', body: JSON.stringify({ admob_pub_id: pub }) }),
  setAdsConfig: (customer: string, devToken: string, loginCustomer?: string) =>
    request<{ ok: boolean }>('/api/google/ads-config', {
      method: 'PUT',
      body: JSON.stringify({ ads_customer: customer, ads_dev_token: devToken, ads_login_customer: loginCustomer ?? '' }),
    }),
  syncNow: () => request<SyncNowResult>('/api/sync/now', { method: 'POST' }),
  dashboard: (days = 30) => request<DashboardData>(`/api/dashboard?days=${days}`),
  calendar: (month?: string) => request<CalendarDay[]>(`/api/calendar${month ? `?month=${month}` : ''}`),
  countries: (days = 30) => request<CountryRow[]>(`/api/countries?days=${days}`),
  reportStats: (q: { by: StatsBy; from?: string; to?: string; limit?: number; include_zero?: boolean }) => {
    const p = new URLSearchParams({ by: q.by })
    if (q.from) p.set('from', q.from)
    if (q.to) p.set('to', q.to)
    if (q.limit) p.set('limit', String(q.limit))
    if (q.include_zero) p.set('include_zero', '1')
    return request<StatsResponse>(`/api/report/stats?${p}`)
  },
  campaigns: (days = 30) => request<CampaignRow[]>(`/api/campaigns?days=${days}`),
  appleApps: (days = 30) => request<AppleAppsResponse>(`/api/apple/apps?days=${days}`),
  createCampaign: (body: CreateCampaignBody) =>
    request<BudgetWriteResult & { id?: number; ads_campaign_id?: string; validated?: boolean }>('/api/campaigns', { method: 'POST', body: JSON.stringify(body) }),
  setCampaignStatus: (id: number, status: 'ENABLED' | 'PAUSED') =>
    request<{ ok: boolean }>(`/api/campaigns/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  setCampaignBudget: (id: number, amountUSD: number) =>
    request<BudgetWriteResult>(`/api/campaigns/${id}/budget`, { method: 'PUT', body: JSON.stringify({ amount_usd: amountUSD }) }),
  fx: () => request<FxInfo>('/api/fx'),
  autopilot: () => request<AutopilotRec[]>('/api/autopilot'),
  budgetPools: () => request<BudgetPool[]>('/api/budget-pools'),
  createBudgetPool: (body: CreateBudgetPoolBody) =>
    request<{ ok: boolean; id: number }>('/api/budget-pools', { method: 'POST', body: JSON.stringify(body) }),
  updateBudgetPool: (id: number, body: UpdateBudgetPoolBody) =>
    request<{ ok: boolean }>(`/api/budget-pools/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteBudgetPool: (id: number) => request<{ ok: boolean }>(`/api/budget-pools/${id}`, { method: 'DELETE' }),
  setCampaignPool: (id: number, poolId: number | null) =>
    request<{ ok: boolean }>(`/api/campaigns/${id}/pool`, { method: 'PUT', body: JSON.stringify({ pool_id: poolId }) }),
  writeLog: (limit = 50) => request<WriteLogRow[]>(`/api/write-log?limit=${limit}`),
  mapCampaign: (id: number, appId: number | null) =>
    request<{ ok: boolean }>(`/api/campaigns/${id}/app`, { method: 'PUT', body: JSON.stringify({ app_id: appId }) }),
  apps: () => request<AppInfo[]>('/api/apps'),
  statsToken: () => request<StatsTokenInfo>('/api/stats-token'),
  statsTokenValue: () => request<{ token: string }>('/api/stats-token/value'),
  issueStatsToken: () => request<{ token: string; preview: string }>('/api/stats-token', { method: 'POST' }),
  deleteStatsToken: () => request<StatsTokenInfo>('/api/stats-token', { method: 'DELETE' }),
  alerts: () => request<AlertRow[]>('/api/alerts'),
  readAlert: (id: number) => request<{ ok: boolean }>(`/api/alerts/${id}/read`, { method: 'PUT' }),
}

// ─── adfusion types ───────────────────────────────────────────────────────

/** What one manual pass brought in (POST /api/sync/now). An AdMob or Ads
 *  error leaves the pass ok; the error is said here and in the run's details. */
export interface SyncNowResult {
  ok: boolean
  admob_rows?: number
  admob_error?: string
  /** true = the pass did not call AdMob (switch off or no publisher ID). */
  admob_skipped?: boolean
  ads_rows?: number
  ads_error?: string
  /** true = google_ads.enabled is off: the pass did not call Google Ads. */
  ads_skipped?: boolean
}

export interface GoogleStatus {
  connected: boolean
  scope: string
  admob_account_id: string
  ads_customer_id: string
  ads_developer_token_set: boolean
  /** Manager (MCC) account id; empty when the ads account is reached directly. */
  ads_login_customer_id: string
  ads_linked: boolean
  client_id_set: boolean
  oauth_configured: boolean
  app_count: number
  campaign_count: number
  metric_days: number
  last_sync_at: string
  /** Settings › AdMob and › Google Ads switches. Off = that side is not
   *  called at all (accounts closed 2026-11); stored figures stay. */
  admob_enabled?: boolean
  ads_enabled?: boolean
}

export interface KPI {
  revenue: number
  cost: number
  profit: number
  roas: number
}

export interface TrendPoint {
  date: string
  revenue: number
  cost: number
  profit: number
}

export interface AppRow {
  app_id: number
  name: string
  platform: string
  revenue: number
  cost: number
  profit: number
  roas: number
  unmapped_cost: number
}

export interface DashboardData {
  today: KPI
  yesterday: KPI
  last7: KPI
  last30: KPI
  trend: TrendPoint[]
  apps: AppRow[] | null
}

export interface CalendarDay {
  date: string
  revenue: number
  cost: number
  profit: number
}

export interface StatsTokenInfo {
  enabled: boolean
  source: 'settings' | 'env' | ''
  preview: string
  path: string
}

export type StatsBy = 'total' | 'day' | 'month' | 'app' | 'country' | 'app_month' | 'day_app_country'

export interface StatsMetrics {
  revenue: number
  cost: number
  profit: number
  roas: number
  impressions: number
  clicks: number
  ad_requests: number
  matched_requests: number
  ctr: number
  ecpm: number
  match_rate: number
}

export interface StatsRow extends StatsMetrics {
  date?: string
  month?: string
  country?: string
  app_id?: number
  app_name?: string
  platform?: string
  package_name?: string
  admob_app_id?: string
}

export interface StatsResponse {
  meta: { currency: string; timezone: string; data_from: string; data_to: string; last_sync_at: string; today_partial: boolean }
  from: string
  to: string
  by: StatsBy
  totals: StatsMetrics
  unmapped_cost: number
  rows: StatsRow[]
}

export interface CountryRow {
  country: string
  revenue: number
  impressions: number
  clicks: number
  ecpm: number
  share: number
}

export interface CampaignRow {
  id: number
  ads_id: string
  name: string
  status: string
  channel_type: string
  /** Which ad network the campaign runs on; absent = google. `ads_id` is
   *  shown without the server's "apple:" prefix. */
  network?: 'google' | 'apple'
  app_id: number | null
  app_name: string | null
  cost: number
  impressions: number
  clicks: number
  conversions: number
  cpa: number
  /** The campaign's current daily budget in dollars; 0 when the sync has
   *  not seen one. */
  daily_budget_usd?: number
  /** The Ads account currency ("KRW"); "USD" until the first sync reads it. */
  currency?: string
  /** The same budget in the account currency, as Google stores it. */
  daily_budget_local?: number
  /** Lifetime spend cap the sync enforces (0 = none), the campaign's spend in
   *  all, and when the sync paused it for reaching the cap. */
  spend_cap_usd?: number
  spend_total_usd?: number
  capped_at?: string
  /** The budget pool the campaign belongs to; null/absent = none. */
  pool_id?: number | null
}

/** One App Store app's Apple Ads results (GET /api/apple/apps). The three
 *  `matched_*` figures and `roas` cover only the days an Apple campaign of the
 *  app ran and the store's revenue is known; `spend_usd` is the whole window. */
export interface AppleAppRow {
  adam_id: string
  name: string
  campaigns: number
  enabled: number
  spend_usd: number
  impressions: number
  taps: number
  installs: number
  /** 0 when there are no installs. */
  cpi: number
  matched_days: number
  matched_spend_usd: number
  matched_proceeds_usd: number
  /** null when no spend fell on a matched day. */
  roas: number | null
}

export interface AppleAppsResponse {
  rows: AppleAppRow[]
  /** The newest day with App Store sales data ("" before the first pull). */
  latest_revenue_date: string
}

/** One member of a budget pool: spend and traffic inside the pool's window. */
export interface PoolCampaign {
  id: number
  name: string
  status: string
  network?: 'google' | 'apple'
  app_name: string
  spent_usd: number
  impressions: number
  clicks: number
  conversions: number
}

/** A budget pool: one total (USD, from `start_date`, to `end_date` when set)
 *  that several campaigns share. The server pauses the members when they have
 *  spent it, and never turns anything on again. */
export interface BudgetPool {
  id: number
  name: string
  total_usd: number
  spent_usd: number
  remaining_usd: number
  start_date: string
  end_date: string | null
  status: 'ACTIVE' | 'CAPPED' | 'ENDED'
  capped_at: string | null
  /** Daily budget of the ENABLED members, in dollars at the newest rate. */
  daily_budget_usd_sum: number
  campaigns: PoolCampaign[]
}

export interface CreateBudgetPoolBody {
  name: string
  total_usd: number
  start_date: string
  end_date?: string
  campaign_ids: number[]
}

/** end_date "" removes the end. */
export interface UpdateBudgetPoolBody {
  name?: string
  total_usd?: number
  end_date?: string
}

/** One write to the ad platform, from the console or the sync. */
export interface WriteLogRow {
  id: number
  /** SQLite UTC text. */
  at: string
  actor: 'console' | 'sync' | string
  action: string
  campaign_id: number | null
  campaign_name: string
  ads_campaign_id: string
  network?: 'google' | 'apple'
  detail: string
  ok: boolean
  error: string
}

/** POST /api/campaigns: an App campaign for installs, created PAUSED.
 *  `validate_only` has Google check the whole request and creates nothing. */
export interface CreateCampaignBody {
  name: string
  store_app_id: string
  app_id?: number
  amount_usd: number
  campaign_type?: 'APP_INSTALLS'
  /** ISO country codes, at least one. */
  countries: string[]
  /** Language codes ("en"); none = every language. */
  languages?: string[]
  /** 1-5 lines, 30 characters each. */
  headlines: string[]
  /** 1-5 lines, 90 characters each. */
  descriptions: string[]
  /** YYYY-MM-DD, the last day the campaign serves. */
  end_date?: string
  /** Pause once the campaign has spent this much in all; 0 = no cap. */
  spend_cap_usd?: number
  validate_only?: boolean
}

/** What a budget write returns: the amount Google actually got, in the
 *  account currency (the USD input is converted and rounded to its unit). */
export interface BudgetWriteResult {
  ok: boolean
  currency?: string
  amount_local?: number
  daily_budget_usd?: number
  usd_per_unit?: number
  rate_date?: string
}

/** The rate the budget forms preview with. `local_per_usd` is 1 for a USD
 *  account; `error` is set when a non-USD account has no rate to show. */
export interface FxInfo {
  currency: string
  local_per_usd: number
  usd_per_unit: number
  /** The step budgets move in, in the account currency (KRW: 1). */
  unit_local: number
  date?: string
  error?: string
  /** Won per USD for showing revenue in won, whatever the Ads currency;
   *  absent when no KRW rate can be had. */
  krw_per_usd?: number
  krw_date?: string
}

export interface AutopilotRec {
  campaign_id: number
  ads_campaign_id: string
  campaign_name: string
  app_name: string
  action: 'pause' | 'scale_budget'
  reason: 'no_conversions' | 'low_roas' | 'high_roas'
  spend_7d: number
  conversions_7d: number
  revenue_7d: number
  roas_7d: number
  current_budget_micros: number
  new_budget_micros: number
  /** The micros are in the Ads account currency; these are the same amounts
   *  in dollars, which is what the page shows and applies. */
  currency?: string
  current_budget_usd?: number
  new_budget_usd?: number
}

export interface AppInfo {
  id: number
  admob_app_id: string
  name: string
  platform: string
  package_name: string
}

export interface AlertRow {
  id: number
  type: string
  severity: string
  title: string
  message: string
  is_read: boolean
  created_at: string
}
